/**
 * Date and Time Utilities
 *
 * Handles conversion from UTC (database) to local timezone
 * and provides consistent formatting across the application.
 */

/** Truncate date to start of minute (floor). Used for runbook actualStart. */
export function truncateToMinuteFloor(date: Date): Date {
    return new Date(Math.floor(date.getTime() / 60000) * 60000);
}

/** Duration in whole minutes between two dates. Uses ceil so we never undercount. */
export function minutesBetween(start: Date, end: Date): number {
    return Math.ceil((end.getTime() - start.getTime()) / 60000);
}

/**
 * Convert UTC date string to local Date object
 * @param utcDateString - UTC date string from database (e.g., "2024-01-15T14:30:00Z")
 * @returns Local Date object
 */
export function utcToLocal(utcDateString: string): Date {
    return new Date(utcDateString);
}

/**
 * Format date for display in user's local timezone
 * @param utcDateString - UTC date string from database
 * @param options - Formatting options
 * @returns Formatted date string
 */
export function formatLocalDate(
    utcDateString: string,
    options: {
        includeTime?: boolean;
        includeTimezone?: boolean;
        format?: 'short' | 'medium' | 'long' | 'full';
    } = {}
): string {
    const {
        includeTime = true,
        includeTimezone = false,
        format = 'medium'
    } = options;

    const localDate = utcToLocal(utcDateString);

    const formatOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: format === 'short' ? 'short' : 'long',
        day: 'numeric',
    };

    if (includeTime) {
        formatOptions.hour = '2-digit';
        formatOptions.minute = '2-digit';
        formatOptions.hour12 = true;
    }

    if (includeTimezone) {
        formatOptions.timeZoneName = 'short';
    }

    return localDate.toLocaleDateString('en-US', formatOptions);
}

/**
 * Format time only in user's local timezone
 * @param utcDateString - UTC date string from database
 * @param includeSeconds - Whether to include seconds
 * @returns Formatted time string
 */
export function formatLocalTime(utcDateString: string, includeSeconds = false): string {
    const localDate = utcToLocal(utcDateString);

    const options: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    };

    if (includeSeconds) {
        options.second = '2-digit';
    }

    return localDate.toLocaleTimeString('en-US', options);
}

/**
 * Get relative time (e.g., "2 hours ago", "in 3 days")
 * @param utcDateString - UTC date string from database
 * @returns Relative time string
 */
export function getRelativeTime(utcDateString: string): string {
    const localDate = utcToLocal(utcDateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - localDate.getTime()) / 1000);

    if (diffInSeconds < 60) {
        return 'just now';
    } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
        const days = Math.floor(diffInSeconds / 86400);
        return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
        return formatLocalDate(utcDateString, { includeTime: false });
    }
}

/**
 * Get user's timezone information
 * @returns Timezone information object
 */
export function getUserTimezone() {
    const now = new Date();
    return {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        offset: now.getTimezoneOffset(), // in minutes
        offsetHours: now.getTimezoneOffset() / 60, // in hours
        isDST: isDaylightSavingTime(now),
    };
}

/**
 * Check if a date is in daylight saving time
 * @param date - Date to check
 * @returns True if in DST
 */
function isDaylightSavingTime(date: Date): boolean {
    const jan = new Date(date.getFullYear(), 0, 1);
    const jul = new Date(date.getFullYear(), 6, 1);
    return date.getTimezoneOffset() < Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
}

/**
 * Convert local date to UTC for sending to database
 * @param localDate - Local Date object
 * @returns UTC date string for database
 */
export function localToUtc(localDate: Date): string {
    return localDate.toISOString();
}

/**
 * Format date for different locales
 * @param utcDateString - UTC date string from database
 * @param locale - Locale code (e.g., 'en-US', 'es-ES', 'fr-FR', 'de-DE')
 * @param options - Formatting options
 * @returns Formatted date string in specified locale
 */
export function formatDateForLocale(
    utcDateString: string,
    locale: string = 'en-US',
    options: Intl.DateTimeFormatOptions = {}
): string {
    const localDate = utcToLocal(utcDateString);

    const defaultOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    };

    return localDate.toLocaleDateString(locale, { ...defaultOptions, ...options });
}





























